import * as THREE from 'three/webgpu';
import { DEFAULT_WORLD_THEME, type WorldThemeDefinition } from './worldTheme';

function mod289(x: number): number {
  return x - Math.floor(x / 289) * 289;
}

function permute(x: number): number {
  return mod289(((x * 34) + 10) * x);
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function terrace(value: number, height: number, softness: number): number {
  const scaled = value / height;
  const base = Math.floor(scaled);
  const frac = scaled - base;
  const eased = smoothstep(0.5 - softness, 0.5 + softness, frac);
  return (base + eased) * height;
}

function directionalDistance(px: number, pz: number, angleDeg: number): number {
  const angle = THREE.MathUtils.degToRad(angleDeg);
  return px * Math.cos(angle) + pz * Math.sin(angle);
}

function snoise3(x: number, y: number, z: number): number {
  const F3 = 1 / 3;
  const G3 = 1 / 6;

  const s = (x + y + z) * F3;
  const i = Math.floor(x + s);
  const j = Math.floor(y + s);
  const k = Math.floor(z + s);
  const t = (i + j + k) * G3;

  const x0 = x - (i - t);
  const y0 = y - (j - t);
  const z0 = z - (k - t);

  let i1: number;
  let j1: number;
  let k1: number;
  let i2: number;
  let j2: number;
  let k2: number;

  if (x0 >= y0) {
    if (y0 >= z0) {
      i1 = 1; j1 = 0; k1 = 0; i2 = 1; j2 = 1; k2 = 0;
    } else if (x0 >= z0) {
      i1 = 1; j1 = 0; k1 = 0; i2 = 1; j2 = 0; k2 = 1;
    } else {
      i1 = 0; j1 = 0; k1 = 1; i2 = 1; j2 = 0; k2 = 1;
    }
  } else if (y0 < z0) {
    i1 = 0; j1 = 0; k1 = 1; i2 = 0; j2 = 1; k2 = 1;
  } else if (x0 < z0) {
    i1 = 0; j1 = 1; k1 = 0; i2 = 0; j2 = 1; k2 = 1;
  } else {
    i1 = 0; j1 = 1; k1 = 0; i2 = 1; j2 = 1; k2 = 0;
  }

  const x1 = x0 - i1 + G3;
  const y1 = y0 - j1 + G3;
  const z1 = z0 - k1 + G3;
  const x2 = x0 - i2 + 2 * G3;
  const y2 = y0 - j2 + 2 * G3;
  const z2 = z0 - k2 + 2 * G3;
  const x3 = x0 - 1 + 3 * G3;
  const y3 = y0 - 1 + 3 * G3;
  const z3 = z0 - 1 + 3 * G3;

  const ii = mod289(i & 255);
  const jj = mod289(j & 255);
  const kk = mod289(k & 255);

  const gi0 = permute(permute(permute(kk) + jj) + ii) % 12;
  const gi1 = permute(permute(permute(kk + k1) + jj + j1) + ii + i1) % 12;
  const gi2 = permute(permute(permute(kk + k2) + jj + j2) + ii + i2) % 12;
  const gi3 = permute(permute(permute(kk + 1) + jj + 1) + ii + 1) % 12;

  const grad3 = [
    [1, 1, 0], [-1, 1, 0], [1, -1, 0], [-1, -1, 0],
    [1, 0, 1], [-1, 0, 1], [1, 0, -1], [-1, 0, -1],
    [0, 1, 1], [0, -1, 1], [0, 1, -1], [0, -1, -1],
  ];

  const dot = (gi: number, dx: number, dy: number, dz: number) => {
    const g = grad3[gi];
    return g[0] * dx + g[1] * dy + g[2] * dz;
  };

  let n = 0;
  let t0 = 0.5 - x0 * x0 - y0 * y0 - z0 * z0;
  if (t0 >= 0) {
    t0 *= t0;
    n += t0 * t0 * dot(gi0, x0, y0, z0);
  }
  let t1 = 0.5 - x1 * x1 - y1 * y1 - z1 * z1;
  if (t1 >= 0) {
    t1 *= t1;
    n += t1 * t1 * dot(gi1, x1, y1, z1);
  }
  let t2 = 0.5 - x2 * x2 - y2 * y2 - z2 * z2;
  if (t2 >= 0) {
    t2 *= t2;
    n += t2 * t2 * dot(gi2, x2, y2, z2);
  }
  let t3 = 0.5 - x3 * x3 - y3 * y3 - z3 * z3;
  if (t3 >= 0) {
    t3 *= t3;
    n += t3 * t3 * dot(gi3, x3, y3, z3);
  }

  return 32 * n;
}

export function sampleTerrainHeight(
  px: number,
  pz: number,
  theme: WorldThemeDefinition = DEFAULT_WORLD_THEME,
): number {
  const dist = Math.sqrt(px * px + pz * pz);
  const angle = Math.atan2(pz, px);
  const clearRadius = theme.terrain.innerClearRadius;
  const landmarkRadius = theme.terrain.landmarkRadius;
  const outerRadius = theme.terrain.outerScatterRadius;

  const outerMask = smoothstep(clearRadius * 0.78, clearRadius + 20, dist);
  const richMask = smoothstep(landmarkRadius * 0.9, outerRadius, dist);
  const shoreline = smoothstep(
    -120,
    160,
    directionalDistance(px, pz, theme.terrain.shorelineAngleDeg) - theme.terrain.shorelineOffset,
  );

  let h = 0;
  h += snoise3(px * 0.015, pz * 0.015, 7) * 1.1 * (1 - outerMask);
  h += (1 - smoothstep(0, clearRadius * 0.7, dist)) * -2.2;

  let macro = 0;
  macro += snoise3(px * 0.0035, pz * 0.0035, 0) * 24;
  macro += snoise3(px * 0.008, pz * 0.008, 1) * 10;
  macro += Math.abs(snoise3(px * 0.014, pz * 0.014, 2)) * 9;
  macro += Math.sin(angle * 5 + dist * 0.022 + snoise3(px * 0.006, pz * 0.006, 3) * 2.5) * 4.5;
  macro = terrace(macro, 5, 0.18);

  const ridgeBands = Math.sin(angle * 7 + dist * 0.035) * 2.5 * richMask;
  const coastalDrop = shoreline * richMask * -13;
  const coastalShelf = shoreline * smoothstep(clearRadius, landmarkRadius + 80, dist) * 3.5;

  h += (macro + ridgeBands + coastalDrop + coastalShelf) * outerMask;
  return h;
}

export function sampleTerrainWorldHeight(
  px: number,
  pz: number,
  theme: WorldThemeDefinition = DEFAULT_WORLD_THEME,
): number {
  return theme.terrain.baseY + sampleTerrainHeight(px, pz, theme);
}

export function sampleTerrainNormal(
  px: number,
  pz: number,
  theme: WorldThemeDefinition = DEFAULT_WORLD_THEME,
): THREE.Vector3 {
  const eps = 1;
  const left = sampleTerrainWorldHeight(px - eps, pz, theme);
  const right = sampleTerrainWorldHeight(px + eps, pz, theme);
  const down = sampleTerrainWorldHeight(px, pz - eps, theme);
  const up = sampleTerrainWorldHeight(px, pz + eps, theme);
  return new THREE.Vector3(left - right, eps * 2, down - up).normalize();
}

export function sampleTerrainSlope(
  px: number,
  pz: number,
  theme: WorldThemeDefinition = DEFAULT_WORLD_THEME,
): number {
  return 1 - sampleTerrainNormal(px, pz, theme).y;
}
